import {
  AnchorHTMLAttributes,
  HtmlHTMLAttributes,
  isValidElement,
  PropsWithChildren,
  ReactNode,
} from "react";
import { clsx } from "clsx";
import { Tooltip as RadixTooltip } from "radix-ui";

import { twMerge } from "@/design-system/twMerge";

export type TooltipProps = RadixTooltip.TooltipProps & {
  title?: ReactNode;
  content?: ReactNode;
  ctaSection?: ReactNode;
  contentProps?: Partial<RadixTooltip.TooltipContentProps>;
  triggerProps?: Partial<RadixTooltip.TooltipTriggerProps>;
};

const INTERACTIVE_TRIGGER_DISPLAY_NAMES = new Set(["Button", "IconButton"]);

function triggerIsInteractiveElement(children: ReactNode): boolean {
  if (!isValidElement(children)) return false;
  const { type } = children;
  if (type === "button" || type === "a") return true;
  const displayName = (type as { displayName?: string })?.displayName;
  return (
    typeof displayName === "string" &&
    INTERACTIVE_TRIGGER_DISPLAY_NAMES.has(displayName)
  );
}

export function Tooltip({
  children,
  content,
  title,
  ctaSection,
  contentProps,
  triggerProps,
  ...rest
}: TooltipProps) {
  const { className: contentClassName, ...contentPropsRest } =
    contentProps || {};

  if (!children) return null;
  // Defensively don't display an empty tooltip.
  if (!title && !content && !ctaSection) return <>{children}</>;

  const asChild =
    triggerProps?.asChild ?? triggerIsInteractiveElement(children);

  return (
    <RadixTooltip.Provider>
      <RadixTooltip.Root {...rest} delayDuration={300}>
        <RadixTooltip.Trigger
          {...triggerProps}
          asChild={asChild}
          className="cursor-pointer"
        >
          {children}
        </RadixTooltip.Trigger>
        <RadixTooltip.Portal>
          <RadixTooltip.Content
            side="top"
            sideOffset={8}
            className={twMerge(
              clsx(
                "bg-surface-inverse text-body-sm text-textDefaultInverse data-[state=delayed-open]:animate-fadeIn data-[state=closed]:animate-fadeOut z-tooltip max-w-70 rounded-md px-3 py-2 break-words",
                contentClassName,
              ),
            )}
            {...contentPropsRest}
          >
            {title && <TooltipTitle>{title}</TooltipTitle>}
            {content && <TooltipContentText>{content}</TooltipContentText>}
            {ctaSection && <div className="mt-3">{ctaSection}</div>}
          </RadixTooltip.Content>
        </RadixTooltip.Portal>
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  );
}

type TooltipSubComponentProps<T = unknown> = PropsWithChildren<T>;

function TooltipTitle({ children }: TooltipSubComponentProps) {
  return <div className="text-heading-xs mb-1">{children}</div>;
}

function TooltipContentText({ children }: TooltipSubComponentProps) {
  return <div>{children}</div>;
}

export function TooltipCta({
  children,
  ...rest
}: TooltipSubComponentProps<HtmlHTMLAttributes<HTMLButtonElement>>) {
  return (
    <button
      {...rest}
      className="text-label-sm cursor-pointer border-none outline-none hover:underline"
    >
      {children}
    </button>
  );
}

export function TooltipLinkCta({
  children,
  ...rest
}: TooltipSubComponentProps<AnchorHTMLAttributes<HTMLAnchorElement>>) {
  return (
    <a
      className="text-label-sm cursor-pointer border-none outline-none hover:underline"
      target="_blank"
      rel="noopener noreferrer"
      {...rest}
    >
      {children}
    </a>
  );
}

type ConditionalTooltipProps = TooltipProps & {
  condition: boolean;
  children: ReactNode;
};
export function ConditionalTooltip({
  condition,
  children,
  ...rest
}: ConditionalTooltipProps) {
  if (condition) {
    return (
      <Tooltip {...rest} triggerProps={{ ...rest.triggerProps, asChild: true }}>
        <span>{children}</span>
      </Tooltip>
    );
  }

  return <>{children}</>;
}
